import React, { useEffect, useRef, useState } from 'react';
import ThemeSelector from './themeSelector.component';
import { PlusIcon } from '@common_components/icons/plus.icon';
import EngagementList from './engagementList.component';
import { Theme } from 'interfaces/theme.interface';
import InputText from '@common_components/inputs/textInput.component';
import { useTranslation } from 'react-i18next';
import Button from '@common_components/buttons/button.component';
import { EngagementIcon } from '@common_components/icons/engagement.icon';
import { TitleIcon } from '@common_components/icons/title.icon';
import { Engagement } from 'interfaces/engagement.interface';
import { Setting } from 'interfaces/fieldSetting.interface';
import SelectInput from '@common_components/inputs/selects/selectInput.component';
import { EyeIcon } from '@common_components/icons/eye.icon';
import { CrossIcon } from '@common_components/icons/cross.icon';
import { useSelector } from 'react-redux';
import { dayjs, FORMAT_DATE_API, GroupedOption, LanguageService, RootState, ThemeIcon, ToggleButton, usePersistantState, User, UUID } from '@umanforge/common';
import { GlobalService } from 'services/global.service';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';
import { checkUserParticipation } from 'services/right.service';
import EventSearchContainer from './search/eventSearchContainerComplete.component';
import { FilterProvider, useFilter } from 'components/FilterSection/filterProvider';
import { FilterButton } from 'components/FilterSection/filterButton.component';
import { FilterPanel } from 'components/FilterSection/filterPanel.component';
import { KEY } from 'constants/local_storage_key.constant';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';

interface Props {
  engagements: Engagement[];
  themesOptions: Theme[];
  isActiveField: boolean;
  isLoading?: boolean;
  isViewMode?: boolean;
  isArchived?: boolean;
  themesSelected: string | null;
  eventName: string | undefined;
  customSettings: Setting[];
  dynamicFields: Record<string, string>;
  updateDynamicField: ( fieldId: string, value: string ) => void
  eventManagerOptions: GroupedOption<{ id: UUID, name: string }>[];
  updateEngagement: ( index: number, key: keyof Engagement, value: string[] | string | Date ) => void;
  removeEngagement: ( index: number ) => void;
  setThemeSelected: ( themeId: string ) => void;
  setEventName: ( ( value: string ) => void ) | undefined;
  addEngagement: () => void;
  who: string;
  setWho: ( value: string ) => void;
  where: string;
  setWhere: ( value: string ) => void;
  when: string;
  setWhen: ( value: string ) => void;
  what: string;
  setWhat: ( value: string ) => void;
  why: string;
  setWhy: ( value: string ) => void;
  isRecurrent: boolean;
  setIsRecurrent: ( value: boolean ) => void;
  isHighlighted: boolean;
  setIsHighlighted: ( value: boolean ) => void;
  eventManagerId?: string;
  referenceEventId?: string;
  referenceEventTitle?: string;
  onReferenceEventClick?: ( id: string ) => void;
  onReferenceEventLinkClick?: ( id: string ) => void;
  isAdminOfEventManager?: boolean;
  currentEventId?: string;
  errorMessage: string | null;
}

const LoadingScreen: React.FC = () => {

  const { t } = useTranslation();

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white-base bg-opacity-70 z-50 rounded-xl">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mb-3"></div>
        <p className="text-gray-700 font-medium">{t( 'translation:loading' )}</p>
      </div>
    </div>
  );
};

interface WindowWithPendingFieldSettings extends Window {
  _pendingFieldSettings?: Record<string, string>;
}

const ReferenceEventDisplay: React.FC<{
  referenceEventId?: string;
  referenceEventTitle?: string;
}> = ( { referenceEventId, referenceEventTitle } ) => {
  const { t } = useTranslation();

  if ( !referenceEventId || !referenceEventTitle ) {return null;}

  return (
    <div className='flex items-center animate-fadeIn bg-green-50 border border-green-200 rounded-lg p-3 w-full max-w-full'>
      <div className='flex items-center gap-2 min-w-0 w-full'>
        <div className='bg-green-100 text-green-700 p-1.5 rounded-full flex-shrink-0'>
          <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
            <path fillRule='evenodd' d='M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z' clipRule='evenodd' />
          </svg>
        </div>
        <div className='flex flex-col min-w-0 w-full'>
          {( () => {
            const title = referenceEventTitle || '';
            const displayTitle = title;

            return (
              <>
                <div className='text-green-700 font-semibold text-sm break-words whitespace-normal overflow-hidden'>
                  {displayTitle
                    ? t( 'translation:linked_to_reference_event_named', { eventName: displayTitle } )
                    : t( 'translation:linked_to_reference_event' )}
                </div>
                <div className='text-green-600 text-xs break-words whitespace-normal overflow-hidden'>
                  {displayTitle
                    ? t( 'translation:fields_prefilled_from_reference_named' )
                    : t( 'translation:fields_prefilled_from_reference' )}
                </div>
              </>
            );
          } )()}
        </div>
      </div>
    </div>
  );
};

const NoThemesAvailable: React.FC<{
  referenceEventId?: string;
  referenceEventTitle?: string;
  eventName: string | undefined;
  isRecurrent: boolean;
}> = ( { referenceEventId, referenceEventTitle, eventName, isRecurrent } ) => {
  const { t } = useTranslation();
  return (
    <>
      <div className='filter grayscale opacity-70 pointer-events-none font-texte'>
        <div className='flex gap-3 mb-2 px-5 items-center font-comfortaa bg-orange-100 text-orange-600 rounded-xl w-fit p-2'>
          <TitleIcon size={5}/>
          <h1>{t( 'common:title' )}</h1>
        </div>

        {referenceEventId && referenceEventTitle && (
          <div className='flex items-center mb-3 pl-5 gap-2'>
            <div className='bg-blue-100 text-blue-600 px-3 py-1 rounded-lg text-xs font-medium'>
              {t( 'translation:reference_event' )}
            </div>
            <div className='bg-blue-50 rounded-lg border border-blue-200 px-3 py-1 flex items-center'>
              <div className='text-blue-700 font-medium'>
                {referenceEventTitle}
              </div>
            </div>
          </div>
        )}

        <div className='flex items-center gap-3'>
          <InputText isRequired disable={true} value={eventName} maxCharacter={ MAX_LENGTH_ENTITY.EVENT_NAME } onChange={() => {/* vide intentionnellement */}}/>
          <div className='w-1/4 flex rounded-md border gap-2'>
            <ToggleButton
              label={t( 'translation:recurrent_event' )}
              isSelected={isRecurrent}
              onClick={() => {/* vide intentionnellement */}}
            />
          </div>
          <div className='flex items-center'>
            <Button
              label={t( 'translation:open_search' )}
              padding='py-2 px-4'
              textColor='text-blue-700'
              roundedRadius='rounded-lg'
              textColorHover='hover:text-blue-700'
              hoverBorderColor='hover:border-transparent'
              color='bg-blue-100'
              shadow='shadow-none'
              colorHover='hover:bg-blue-200'
              icon={<EyeIcon size={4} />}
              onClick={() => {/* vide intentionnellement */}}
            />
          </div>
        </div>
      </div>

      <div className='filter grayscale opacity-70 pointer-events-none font-texte'>
        <div className='flex gap-3 mb-2 px-5 items-center font-comfortaa bg-orange-100 text-orange-600 rounded-xl w-fit p-2'>
          <ThemeIcon className='h-5 w-5'/>
          <h1>{t( 'common:theme' )}</h1>
        </div>

        <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 flex justify-center items-center">
          <div className="text-gray-400 flex items-center gap-2">
            <ThemeIcon className="h-4 w-4 text-gray-400" />
            {t( 'translation:no_theme_available' )}
          </div>
        </div>
      </div>

      <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none">
        <div className="bg-white border-2 border-red-300 rounded-xl shadow-xl p-6 max-w-md pointer-events-auto">
          <div className="flex flex-col items-center mb-4">
            <div className="bg-red-100 p-5 rounded-full mb-4 border-2 border-red-200 shadow-md">
              <ThemeIcon className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-center text-red-700 mb-3">
              { t( 'translation:no_theme_available' ) }
            </h2>
            <p className="text-center text-gray-700">
              { t( 'translation:cannot_create_event_without_theme' ) }
            </p>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-center text-gray-500">
                { t( 'translation:please_create_theme_first' ) }
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const EngagementSection: React.FC<{
  isArchived?: boolean;
  isViewMode?: boolean;
  showSearchSection: boolean;
  engagements: Engagement[];
  isAdminOfEventManager?: boolean;
  me: User | null;
  addEngagement: () => void;
  eventManagerOptions: GroupedOption<{ id: UUID, name: string }>[];
  updateEngagement: ( index: number, key: keyof Engagement, value: string[] | string | Date ) => void;
  removeEngagement: ( index: number ) => void;
  errorMessage: string | null;
}> = ( {
  isArchived,
  isViewMode,
  showSearchSection,
  engagements,
  isAdminOfEventManager,
  me,
  addEngagement,
  eventManagerOptions,
  updateEngagement,
  removeEngagement,
  errorMessage
} ) => {
  const { t } = useTranslation();
  const [ selectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  if ( showSearchSection ) {return null;}

  return (
    <div>
      <div className='flex items-center justify-between mb-2'>
        <div className='flex gap-3 items-center font-comfortaa bg-orange-100 text-orange-600 rounded-xl w-fit py-1 pl-2 pr-4'>
          <EngagementIcon className='ml-3' size={5}/>
          <h1 className='flex items-center h-8'>{t( 'translation:engagement' )}</h1>
        </div>
        {!( isArchived || isViewMode ) && checkUserParticipation( me, selectedSectorId, isAdminOfEventManager, engagements ) && (
          <Button
            label={t( 'translation:add_an_engagement' )}
            icon={<PlusIcon className='group-hover:rotate-[360deg] transition-transform duration-500' size={5} />}
            padding='py-2 px-4'
            textColor='text-green-700'
            roundedRadius='rounded-xl'
            textColorHover='hover:text-green-700'
            hoverBorderColor='hover:border-transparent'
            color='bg-green-100'
            shadow='shadow-none'
            colorHover='hover:bg-green-200'
            onClick={addEngagement}
          />
        )}
      </div>
      <div className='flex flex-col w-full mb-5 gap-2 bg-white border rounded-xl'>
        <div className='flex flex-col w-full px-4'>
          <EngagementList
            isArchived={isArchived || isViewMode}
            isViewMode={isViewMode}
            engagements={engagements}
            eventManagerOptions={ eventManagerOptions }
            updateEngagement={updateEngagement}
            removeEngagement={removeEngagement}
            isAdminOfEventManager={isAdminOfEventManager}
          />
        </div>
        { errorMessage != null && (
          <div className="p-2">
            <div className="flex items-center w-full gap-2 py-2 px-4 text-red-600 bg-red-50 border border-red-200 rounded-md shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex flex-col">
                <span className="font-medium">{ errorMessage }</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdditionalFieldsSection: React.FC<{
  showSearchSection: boolean;
  isArchived?: boolean;
  isViewMode?: boolean;
  allInputsDisabled: boolean;
  isActiveField: boolean;
  customSettings: Setting[];
  dynamicFields: Record<string, string>;
  updateDynamicField: ( fieldId: string, value: string ) => void;
  who: string;
  setWho: ( value: string ) => void;
  where: string;
  setWhere: ( value: string ) => void;
  when: string;
  setWhen: ( value: string ) => void;
  what: string;
  setWhat: ( value: string ) => void;
  why: string;
  setWhy: ( value: string ) => void;
  currentEventId?: string;
}> = ( {
  showSearchSection,
  isArchived,
  isViewMode,
  isActiveField,
  allInputsDisabled,
  customSettings,
  dynamicFields,
  updateDynamicField,
  who,
  setWho,
  where,
  setWhere,
  when,
  setWhen,
  what,
  setWhat,
  why,
  setWhy,
  currentEventId
} ) => {

  if ( showSearchSection ) {return null;}

  return (
    <div>
      <FilterProvider>
        <AdditionalFieldsContent
          isActiveField={ isActiveField }
          isArchived={ isArchived }
          isViewMode={ isViewMode }
          allInputsDisabled={ allInputsDisabled }
          customSettings={ customSettings }
          dynamicFields={ dynamicFields }
          updateDynamicField={ updateDynamicField }
          who={ who }
          setWho={ setWho }
          where={ where }
          setWhere={ setWhere }
          when={ when }
          setWhen={ setWhen }
          what={ what }
          setWhat={ setWhat }
          why={ why }
          setWhy={ setWhy }
          currentEventId={ currentEventId }
        />
      </FilterProvider>
    </div>
  );
};

const AdditionalFieldsContent: React.FC<{
  isActiveField: boolean;
  isArchived?: boolean;
  isViewMode?: boolean;
  allInputsDisabled: boolean;
  customSettings: Setting[];
  dynamicFields: Record<string, string>;
  updateDynamicField: ( fieldId: string, value: string ) => void;
  who: string;
  setWho: ( value: string ) => void;
  where: string;
  setWhere: ( value: string ) => void;
  when: string;
  setWhen: ( value: string ) => void;
  what: string;
  setWhat: ( value: string ) => void;
  why: string;
  setWhy: ( value: string ) => void;
  currentEventId?: string;
}> = ( {
  isActiveField,
  isArchived,
  isViewMode,
  allInputsDisabled,
  customSettings,
  dynamicFields,
  updateDynamicField,
  who,
  setWho,
  where,
  setWhere,
  when,
  setWhen,
  what,
  setWhat,
  why,
  setWhy,
  currentEventId
} ) => {
  const { isCollapsed, toggle } = useFilter();
  const containerRef = useRef<HTMLDivElement | null>( null );

  const [ isDisable, setIsDisable ] = useState<boolean>( isArchived || isViewMode || allInputsDisabled );

  const [ whenIsFocus, setWhenIsFocus ] = useState<boolean>( false );
  const [ whenIsDate, setWhenIsDate ] = useState<boolean>( when == null || when == '' || dayjs( when, FORMAT_DATE_API, true ).isValid() || LanguageService.isValidDateOnLocal( when, dayjs.locale() ) );

  useEffect( () => {
    if ( isCollapsed && ( what != '' || when  != '' || where  != '' || who  != '' || why  != '' ) ) {
      toggle();
    }
  }, [ what, when, where, who, why ] );

  useEffect( () => {
    setIsDisable( isArchived || isViewMode || allInputsDisabled );
  }, [ isArchived, isViewMode, allInputsDisabled ] );

  useEffect( () => {
    setWhenIsDate( when == null || when == '' || dayjs( when, FORMAT_DATE_API, true ).isValid() || LanguageService.isValidDateOnLocal( when, dayjs.locale() ) );
  }, [ when ] );

  useEffect( () => {
    if ( isCollapsed && isActiveField ) {
      toggle();
    }
  }, [ isActiveField ] );

  const { t } = useTranslation();

  return (
    <>
      <FilterButton className='w-full' titles={[ t( 'translation:added_fields.hide' ), t( 'translation:added_fields.see' ) ]}></FilterButton>
      <FilterPanel>
        <div ref={containerRef} className="bg-white rounded-xl p-4 border overflow-hidden grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 sm:divide-x-2 sm:divide-orange-200 items-stretch">
          <div className="w-full min-w-0 sm:pr-6 flex flex-col gap-4 sm:h-full">
            <div className="w-full flex flex-col gap-2">
              <label className="text-sm font-medium mb-1 text-left">{t( 'common:what' )}</label>
              <InputText className="w-full" label="" maxCharacter={ MAX_LENGTH_ENTITY.EVENT_WHAT } disable={ isDisable } onChange={setWhat} value={what} />
            </div>

            <div className="w-full flex flex-col gap-2">
              <label className="text-sm font-medium mb-1 text-left">{t( 'common:when' )}</label>

              <div className='flex flex-col gap-2'>
                {
                  !whenIsDate ?
                    <InputText className="w-full" label="" maxCharacter={ MAX_LENGTH_ENTITY.EVENT_WHEN } disable={ true } onChange={setWhen} value={when} />
                    :
                    null
                }
                {
                  (
                    whenIsDate ||
                    !(
                      isArchived ||
                      isViewMode ||
                      allInputsDisabled
                    )
                  ) ?
                    <DatePickerSoloInput
                      placeholder={ t( 'enter_date' ) }
                      border={ !isDisable ? ( `border-[1px] hover:border-black ${ whenIsFocus ? ' border-black ring-1 ring-black' : '' }` ) : '' }
                      width='w-full min-h-11'
                      shadow='shadow'
                      customButtons={ [  { label: t( 'common:clear' ), date: null } ] }
                      inputClassName='outline-none rounded-md'
                      defaultValue={ dayjs( when, [ ...LanguageService.getSupportedLocalFormats( dayjs.locale() ), FORMAT_DATE_API ], true ).toDate() }
                      disable={ isDisable }
                      onFocus={ () => { setWhenIsFocus( true ); } }
                      onBlur={ () => { setWhenIsFocus( false ); } }
                      onChange={ e => {
                        let newDate = '';
                        if ( e ) {
                          newDate = dayjs( e ).format( FORMAT_DATE_API );
                        }
                        setWhen( newDate );
                      } }
                    />
                    :
                    null
                }
              </div>
            </div>

            <div className="w-full flex flex-col gap-2">
              <label className="text-sm font-medium mb-1 text-left">{t( 'common:where' )}</label>
              <InputText className="w-full" label="" maxCharacter={ MAX_LENGTH_ENTITY.EVENT_WHERE } disable={ isDisable } onChange={setWhere} value={where} />
            </div>
          </div>

          <div className="w-full min-w-0 sm:pl-6 flex flex-col gap-4 sm:h-full sm:justify-center">
            <div className="w-full flex flex-col gap-2">
              <label className="text-sm font-medium mb-1 text-left">{t( 'common:who' )}</label>
              <InputText className="w-full" label="" maxCharacter={ MAX_LENGTH_ENTITY.EVENT_WHO } disable={ isDisable } onChange={setWho} value={who} />
            </div>

            <div className="w-full flex flex-col gap-2">
              <label className="text-sm font-medium mb-1 text-left">{t( 'common:why' )}</label>
              <InputText className="w-full" label="" maxCharacter={ MAX_LENGTH_ENTITY.EVENT_WHY } disable={ isDisable } onChange={setWhy} value={why} />
            </div>
          </div>
        </div>
        {customSettings && customSettings.length > 0 && (
          <div className="bg-white rounded-xl flex flex-col gap-4 p-4 border mt-4">
            {customSettings?.map( ( field: Setting ) => {
              return renderCustomField(
                field,
                dynamicFields,
                updateDynamicField,
                isDisable,
                t,
                currentEventId,
              );
            } )}
          </div>
        )}
      </FilterPanel>
    </>
  );
};

const renderCustomField = (
  field: Setting,
  dynamicFields: Record<string, string>,
  updateDynamicField: ( fieldId: string, value: string ) => void,
  isDisabled: boolean,
  t: ( key: string ) => string,
  currentEventId?: string,
) => {
  const fieldId = field.id || '';

  switch ( field.type ) {
  case 'text':
    return (
      <div key={fieldId} className="flex items-center gap-2">
        <div className="flex-1 min-w-0 w-full">
          <label className="text-sm font-medium mb-1">{field.name}</label>
          <InputText
            className="w-full"
            disable={isDisabled}
            label=""
            value={dynamicFields[ fieldId ] || ''}
            maxCharacter={
              field.constraint ?
                (
                  Number( field.constraint ) > MAX_LENGTH_ENTITY.FIELD_SETTING_VALUE ?
                    MAX_LENGTH_ENTITY.FIELD_SETTING_VALUE
                    :
                    Number( field.constraint )
                )
                :
                MAX_LENGTH_ENTITY.FIELD_SETTING_VALUE
            }
            onChange={( val: string ) => {
              updateDynamicField( fieldId, val );
              try {
                if ( !currentEventId || !GlobalService.isValidGuid( currentEventId ) ) {
                  try {
                    const w = window as WindowWithPendingFieldSettings;
                    if ( !w._pendingFieldSettings ) { w._pendingFieldSettings = {}; }
                    w._pendingFieldSettings[ fieldId ] = String( val ?? '' );
                  } catch ( e ) {
                    console.error( 'Error storing pending field setting:', e );
                  }
                }
              } catch ( e ) {
                console.error( 'Error in onChange saving field value:', e );
              }
            }}
            key={fieldId}
          />
        </div>
      </div>
    );
  case 'number': {
    const getMinMax = ( constraint: string | undefined ): { min?: number; max?: number } => {
      if ( !constraint ) {return {};}
      const [ minStr, maxStr ] = constraint.split( ',' );
      return {
        min: minStr !== undefined && minStr !== '' ? Number( minStr ) : undefined,
        max: maxStr !== undefined && maxStr !== '' ? Number( maxStr ) : undefined
      };
    };
    const { min, max } = getMinMax( field.constraint );
    return (
      <div key={fieldId} className="flex items-center gap-2">
        <div className="flex-1 min-w-0 w-full">
          <label className="text-sm font-medium mb-1">{field.name}</label>
          <InputText
            className="w-full min-w-[120px] sm:min-w-0"
            label=""
            type="number"
            disable={isDisabled}
            min={min}
            max={max}
            value={dynamicFields[ fieldId ] || ''}
            onChange={( val: string ) => {
              updateDynamicField( fieldId, val );
              try {
                if ( !currentEventId || !GlobalService.isValidGuid( currentEventId ) ) {
                  try {
                    const w = window as WindowWithPendingFieldSettings;
                    if ( !w._pendingFieldSettings ) { w._pendingFieldSettings = {}; }
                    w._pendingFieldSettings[ fieldId ] = String( val ?? '' );
                  } catch ( e ) {
                    console.error( 'Error storing pending field setting:', e );
                  }
                }
              } catch ( e ) {
                console.error( 'Error in onChange saving field value:', e );
              }
            }}
          />
        </div>
      </div>
    );
  }
  case 'date':
    return (
      <div key={fieldId} className="flex items-center gap-2">
        <div className="flex-1 min-w-0 w-full flex flex-col gap-1">
          <label className="text-sm font-medium">{field.name}</label>
          <input
            type="date"
            disabled={isDisabled}
            className="border rounded-md p-2 w-full"
            value={dynamicFields[ fieldId ] || ''}
            onChange={e => {
              const val = e.target.value;
              updateDynamicField( fieldId, val );
              try {
                if ( !currentEventId || !GlobalService.isValidGuid( currentEventId ) ) {
                  try {
                    const w = window as WindowWithPendingFieldSettings;
                    if ( !w._pendingFieldSettings ) { w._pendingFieldSettings = {}; }
                    w._pendingFieldSettings[ fieldId ] = String( val ?? '' );
                  } catch ( err ) {
                    console.error( 'Error storing pending date field value:', err );
                  }
                }
              } catch ( err ) {
                console.error( 'Error saving date field value:', err );
              }
            }}
          />
        </div>
      </div>
    );
  case 'list': {
    const options = field.constraint ? field.constraint.split( ',' ).map( s => { return s.trim(); } ).filter( Boolean ) : [];
    const selectedValue = ( dynamicFields[ fieldId ] || '' ).toString().trim();
    return (
      <div key={fieldId} className="flex flex-col gap-1">
        <label className="text-sm font-medium">{field.name}</label>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <SelectInput
              className="w-full"
              placeholder={t( 'common:select_option' )}
              label=""
              fetchOptions={() => {
                return Promise.resolve( [
                  {
                    label: '',
                    options: options.map( option => {return { id: option, label: option };} )
                  }
                ] );
              }}
              selected={selectedValue}
              onSelect={val => {
                const next = ( val || '' ).toString().trim();
                updateDynamicField( fieldId, next );
                try {
                  if ( !currentEventId || !GlobalService.isValidGuid( currentEventId ) ) {
                    try {
                      const w = window as WindowWithPendingFieldSettings;
                      if ( !w._pendingFieldSettings ) { w._pendingFieldSettings = {}; }
                      w._pendingFieldSettings[ fieldId ] = String( next ?? '' );
                    } catch ( e ) {
                      console.error( 'Error storing pending list field value:', e );
                    }
                  }
                } catch ( e ) {
                  console.error( 'Error saving list field value:', e );
                }
              }}
              isDisable={isDisabled}
            />
          </div>
          {/* Clear button to allow emptying the list field */}
          <button
            type="button"
            aria-label={t( 'common:clear' )}
            onClick={() => {
              if ( !isDisabled ) {
                updateDynamicField( fieldId, '' );
                try {
                  if ( !currentEventId || !GlobalService.isValidGuid( currentEventId ) ) {
                    try {
                      const w = window as WindowWithPendingFieldSettings;
                      if ( !w._pendingFieldSettings ) { w._pendingFieldSettings = {}; }
                      w._pendingFieldSettings[ fieldId ] = '';
                    } catch ( e ) {
                      console.error( 'Error storing pending clear for list field:', e );
                    }
                  }
                } catch ( e ) {
                  console.error( 'Error clearing field value:', e );
                }
              }
            }}
            className="h-10 w-10 flex-center rounded-md border p-1 bg-white hover:bg-gray-100 text-gray-600"
            disabled={isDisabled}
          >
            <CrossIcon size={4} />
          </button>
        </div>
      </div>
    );
  }
  default:
    return null;
  }
};

const EngagementsSection: React.FC<Props> = ( {
  engagements,
  themesOptions,
  isActiveField,
  isLoading,
  isViewMode,
  isArchived,
  themesSelected,
  eventName,
  customSettings,
  dynamicFields,
  updateDynamicField,
  eventManagerOptions,
  updateEngagement,
  removeEngagement,
  setThemeSelected,
  setEventName,
  addEngagement,
  who,
  setWho,
  where,
  setWhere,
  when,
  setWhen,
  what,
  setWhat,
  why,
  setWhy,
  isRecurrent,
  setIsRecurrent,
  isHighlighted,
  setIsHighlighted,
  eventManagerId,
  referenceEventId,
  referenceEventTitle,
  onReferenceEventClick,
  onReferenceEventLinkClick,
  isAdminOfEventManager,
  currentEventId,
  errorMessage
} ) => {
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const [ selectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const isParticipant = checkUserParticipation( me, selectedSectorId, isAdminOfEventManager, engagements );
  const allInputsDisabled = !isParticipant;
  const { t } = useTranslation();

  const [ showSearchSection, setShowSearchSection ] = useState<boolean>( false );

  return (
    <>
      {isLoading && <LoadingScreen />}
      <div className='font-texte h-full w-full p-4'>

        <div className='flex flex-col gap-5'>

          <div
            className='relative flex flex-col gap-5'>
            {
              !isLoading && ( !themesOptions || themesOptions.length === 0 ) ?
                <NoThemesAvailable
                  referenceEventId={referenceEventId}
                  referenceEventTitle={referenceEventTitle}
                  eventName={eventName}
                  isRecurrent={isRecurrent}
                />
                :
                <>
                  <ReferenceEventDisplay
                    referenceEventId={referenceEventId}
                    referenceEventTitle={referenceEventTitle}
                  />
                  <div className="w-full flex flex-col gap-3">
                    <div className="w-full flex flex-col lg:flex-row lg:items-center lg:gap-4 gap-3">
                      <div className="flex flex-row items-center gap-3 px-5 font-comfortaa bg-orange-100 text-orange-600 rounded-xl w-fit p-2">
                        <TitleIcon size={5} />
                        <h1>{t( 'common:title' )}</h1>
                      </div>

                      <div className="w-full lg:flex-1 lg:min-w-0">
                        <InputText isRequired disable={isArchived || isViewMode || allInputsDisabled} value={eventName} maxCharacter={MAX_LENGTH_ENTITY.EVENT_NAME} onChange={setEventName} />
                      </div>
                    </div>

                    <div className='w-full flex flex-wrap gap-2 justify-start items-center'>
                      <div className='flex rounded-md border'>
                        <ToggleButton
                          disable={isArchived || isViewMode || allInputsDisabled}
                          label={t( 'translation:recurrent_event' )}
                          isSelected={isRecurrent}
                          onClick={() => {return setIsRecurrent( !isRecurrent );}}
                        />
                      </div>

                      <div className='flex rounded-md border'>
                        <ToggleButton
                          disable={isArchived || isViewMode || allInputsDisabled}
                          label={t( 'translation:highlight' )}
                          isSelected={isHighlighted}
                          onClick={() => {return setIsHighlighted( !isHighlighted );}}
                        />
                      </div>

                      {!( isArchived || isViewMode ) && (
                        <div className='flex'>
                          <Button
                            label={showSearchSection ? t( 'translation:hide_search' ) : t( 'translation:open_search' )}
                            padding='py-2 px-4'
                            textColor='text-blue-700'
                            roundedRadius='rounded-lg'
                            textColorHover='hover:text-blue-700'
                            hoverBorderColor='hover:border-transparent'
                            color='bg-blue-100'
                            shadow='shadow-none'
                            colorHover='hover:bg-blue-200'
                            icon={<EyeIcon size={4} />}
                            onClick={() => {return setShowSearchSection( !showSearchSection );}}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {!showSearchSection && (
                    <div className="w-full flex flex-col mt-4">
                      <div className="flex flex-col sm:flex-row sm:items-center items-start gap-3">
                        <div className="flex flex-row items-center gap-3 px-5 font-comfortaa bg-orange-100 text-orange-600 rounded-xl w-fit p-2">
                          <ThemeIcon className="h-5 w-5" />
                          <h1>{ t( 'common:theme' ) }</h1>
                        </div>
                        <div className="flex-1 min-w-0 w-full sm:w-1/2">
                          <ThemeSelector
                            isArchived={ isArchived || isViewMode || allInputsDisabled }
                            themesOptions={ Array.isArray( themesOptions ) ? [ ...themesOptions ].sort( ( a, b ) => { return a.name.localeCompare( b.name ); } ) : themesOptions }
                            themesSelected={ themesSelected }
                            toggleThemeSelection={ setThemeSelected }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                </>
            }
          </div>

          {!showSearchSection && (
            <EngagementSection
              isArchived={isArchived}
              isViewMode={isViewMode}
              showSearchSection={showSearchSection}
              engagements={engagements}
              isAdminOfEventManager={isAdminOfEventManager}
              me={me}
              addEngagement={addEngagement}
              eventManagerOptions={eventManagerOptions}
              updateEngagement={updateEngagement}
              removeEngagement={removeEngagement}
              errorMessage={errorMessage}
            />
          )}

          {!showSearchSection && (
            <AdditionalFieldsSection
              isActiveField={ isActiveField }
              showSearchSection={showSearchSection}
              isArchived={isArchived}
              isViewMode={isViewMode}
              allInputsDisabled={allInputsDisabled}
              customSettings={customSettings}
              dynamicFields={dynamicFields}
              updateDynamicField={updateDynamicField}
              who={who}
              setWho={setWho}
              where={where}
              setWhere={setWhere}
              when={when}
              setWhen={setWhen}
              what={what}
              setWhat={setWhat}
              why={why}
              setWhy={setWhy}
              currentEventId={currentEventId}
            />
          )}

          {showSearchSection && (
            <div className='mt-6 search-section-enter'>
              <EventSearchContainer
                eventManagerId={eventManagerId}
                onEventSelect={( id: string ) => {
                  if ( onReferenceEventClick ) {
                    onReferenceEventClick( id );
                  }
                }}
                onEventLinkClick={onReferenceEventLinkClick}
                onClose={() => {return setShowSearchSection( false );}}
                currentEventId={currentEventId}
              />
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default EngagementsSection;
